/**
 * DatePicker Component
 * Selector de fecha usando Calendar de shadcn
 */

import * as React from "react";
import { format, parse } from "date-fns";
import { es, enUS, ptBR, type Locale } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const locales: Record<string, Locale> = {
  es: es,
  en: enUS,
  pt: ptBR,
};

interface DatePickerProps {
  value?: string; // formato YYYY-MM-DD
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Fecha mínima seleccionable */
  fromDate?: Date;
  /** Fecha máxima seleccionable */
  toDate?: Date;
}

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  fromDate,
  toDate = new Date(),
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  // Extraer código de idioma base (es-HN -> es, en-US -> en)
  const langCode = i18n.language?.split("-")[0] || "es";
  const locale = locales[langCode] || es;

  // Convertir string YYYY-MM-DD a Date
  const dateValue = React.useMemo(() => {
    if (!value) return undefined;
    try {
      return parse(value, "yyyy-MM-dd", new Date());
    } catch {
      return undefined;
    }
  }, [value]);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {dateValue
            ? format(dateValue, "dd/MM/yyyy", { locale })
            : placeholder || t("common.selectDate", "Seleccionar fecha")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          key={i18n.language}
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          disabled={(date) => {
            if (toDate && date > toDate) return true;
            if (fromDate && date < fromDate) return true;
            return false;
          }}
          defaultMonth={dateValue}
          captionLayout="dropdown"
          fromYear={1920}
          toYear={new Date().getFullYear()}
          locale={locale}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
